import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiBarChart2, FiCalendar, FiTrendingUp, FiUsers,
  FiBookOpen, FiAward, FiFilter, FiMapPin, FiDownload
} from 'react-icons/fi';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  Tooltip, Legend, Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { reuniaoJovensService } from '../../services/api';
import './ReuniaoJovens.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

// Plugin inline: exibe o valor exato acima de cada ponto
const datalabelsInline = {
  id: 'rjDatalabels',
  afterDatasetsDraw(chart) {
    // Permite desativar por chart via options.plugins.rjDatalabels.display = false
    if (chart.options?.plugins?.rjDatalabels?.display === false) return;
    const { ctx, data } = chart;
    data.datasets.forEach((dataset, i) => {
      const meta = chart.getDatasetMeta(i);
      if (meta.hidden) return;
      meta.data.forEach((pt, idx) => {
        const val = dataset.data[idx];
        if (val == null || val === 0) return;
        const txt = String(val);
        const color = dataset.borderColor || '#18181b';

        ctx.save();
        ctx.font = 'bold 10px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // Fundo branco arredondado
        const tw = ctx.measureText(txt).width;
        const pad = 3;
        ctx.fillStyle = 'rgba(255,255,255,0.88)';
        ctx.beginPath();
        ctx.roundRect(pt.x - tw / 2 - pad, pt.y - 18, tw + pad * 2, 14, 3);
        ctx.fill();

        // Texto
        ctx.fillStyle = color;
        ctx.fillText(txt, pt.x, pt.y - 11);
        ctx.restore();
      });
    });
  },
};
ChartJS.register(datalabelsInline);

const CAT_LABELS = {
  MENINAS: 'Meninas', MOCAS: 'Moças', MENINOS: 'Meninos',
  MOCOS: 'Moços', CASADOS: 'Casados',
};
const CAT_COR = {
  MENINAS: '#ec4899', MOCAS: '#a855f7', MENINOS: '#3b82f6',
  MOCOS: '#0ea5e9', CASADOS: '#f59e0b',
};
const SERIES_EXTRA = {
  total:       { label: 'Total Geral',     cor: '#18181b' },
  totalJovens: { label: 'Total de Jovens', cor: '#6366f1' },
  recitaram:   { label: 'Recitaram',       cor: '#22c55e' },
};
const CATS_JOVENS = ['MENINAS', 'MOCAS', 'MENINOS', 'MOCOS'];

const formatarMes = (chave) => {
  if (!chave) return '';
  const [ano, mes] = chave.split('-');
  const meses = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  return `${meses[parseInt(mes, 10) - 1]}/${ano.slice(2)}`;
};

const formatDataCurta = (iso) => {
  if (!iso) return '';
  const s = String(iso).substring(0, 10);
  const [a, m, d] = s.split('-').map(Number);
  return `${String(d).padStart(2,'0')}/${String(m).padStart(2,'0')}`;
};

const ReuniaoJovensDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('12m');
  const [congFiltro, setCongFiltro] = useState('');

  // Filtros do gráfico de linha
  const [chartPeriodo, setChartPeriodo] = useState('todos');
  const [seriesSel, setSeriesSel] = useState(['total', 'recitaram']);
  const [metricaMensal, setMetricaMensal] = useState('presentes'); // presentes | recitaram
  const chartRef = useRef(null);

  useEffect(() => {
    const carregar = async () => {
      try {
        setLoading(true);
        const params = {};
        if (periodo !== 'todos') {
          const meses = periodo === '3m' ? 3 : periodo === '6m' ? 6 : periodo === '12m' ? 12 : 24;
          const ini = new Date();
          ini.setMonth(ini.getMonth() - meses);
          params.dataInicio = ini.toISOString().split('T')[0];
        }
        if (congFiltro) params.congregacao = congFiltro;
        const resp = await reuniaoJovensService.dashboard(params);
        setStats(resp.data);
      } finally {
        setLoading(false);
      }
    };
    carregar();
  }, [periodo, congFiltro]);

  const toggleSerie = useCallback((key) => {
    setSeriesSel((prev) =>
      prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]
    );
  }, []);

  // Dados filtrados para o gráfico de linha
  const chartData = useMemo(() => {
    if (!stats?.serieTemporal?.length) return null;
    let pts = [...stats.serieTemporal].sort((a, b) =>
      String(a.data).localeCompare(String(b.data))
    );

    if (chartPeriodo !== 'todos') {
      const meses = chartPeriodo === '3m' ? 3 : chartPeriodo === '6m' ? 6 : 12;
      const corte = new Date();
      corte.setMonth(corte.getMonth() - meses);
      pts = pts.filter((p) => new Date(p.data) >= corte);
    }

    if (!pts.length) return null;

    const labels = pts.map((p) => {
      const dt = formatDataCurta(p.data);
      return `${dt} ${p.congregacao?.substring(0, 3) || ''}`;
    });

    const datasets = [];

    if (seriesSel.includes('total')) {
      datasets.push({
        label: 'Total Geral',
        data: pts.map((p) => p.total),
        borderColor: '#18181b',
        backgroundColor: 'rgba(24,24,27,0.08)',
        fill: true,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2.5,
      });
    }
    if (seriesSel.includes('totalJovens')) {
      datasets.push({
        label: 'Total de Jovens',
        data: pts.map((p) =>
          CATS_JOVENS.reduce((acc, cat) => acc + (p.categorias?.[cat]?.compareceram || 0), 0)
        ),
        borderColor: '#6366f1',
        backgroundColor: 'rgba(99,102,241,0.08)',
        fill: false,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2,
        borderDash: [6, 3],
      });
    }
    if (seriesSel.includes('recitaram')) {
      datasets.push({
        label: 'Recitaram',
        data: pts.map((p) => p.recitaram),
        borderColor: '#22c55e',
        backgroundColor: 'rgba(34,197,94,0.08)',
        fill: false,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2,
      });
    }

    Object.keys(CAT_LABELS).forEach((cat) => {
      if (!seriesSel.includes(cat)) return;
      datasets.push({
        label: CAT_LABELS[cat],
        data: pts.map((p) => p.categorias?.[cat]?.compareceram || 0),
        borderColor: CAT_COR[cat],
        backgroundColor: `${CAT_COR[cat]}15`,
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
        borderWidth: 2,
      });
    });

    return { labels, datasets };
  }, [stats, chartPeriodo, seriesSel]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    layout: { padding: { top: 20 } },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#18181b',
        titleFont: { size: 13 },
        bodyFont: { size: 12 },
        padding: 10,
        cornerRadius: 8,
      },
      rjDatalabels: {},
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 }, maxRotation: 45, color: '#71717a' },
      },
      y: {
        beginAtZero: true,
        grid: { color: '#f4f4f5' },
        ticks: { font: { size: 11 }, color: '#71717a' },
      },
    },
  }), []);

  const downloadChart = useCallback(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const url = chart.toBase64Image('image/png', 1);
    const a = document.createElement('a');
    a.href = url;
    a.download = `evolucao-reuniao-jovens-${new Date().toISOString().split('T')[0]}.png`;
    a.click();
  }, []);

  const evolucaoChart = useMemo(() => {
    if (!stats?.evolucaoMensal?.length) return null;
    const periodos = stats.evolucaoMensal;
    const valorDe = (p) => (
      metricaMensal === 'recitaram'
        ? (p.mediaRecitaram ?? 0)
        : (p.media ?? p.total ?? 0)
    );
    const maxValor = Math.max(...periodos.map(valorDe), 1);
    return { periodos, maxValor, valorDe };
  }, [stats, metricaMensal]);

  const congregacoes = stats?.porCongregacao || [];
  const maxCong = congregacoes.length > 0
    ? Math.max(...congregacoes.map((c) => c.mediaCompareceram), 1) : 1;
  const liderCong = congregacoes[0];

  const maxCatMedia = useMemo(() => {
    if (!stats?.porCategoria?.length) return 1;
    return Math.max(...stats.porCategoria.map((c) => c.mediaCompareceram || 0), 1);
  }, [stats]);

  const congDisponiveis = stats?.congDisponiveis || [];

  if (loading) return <div className="rj-page"><div className="rj-empty">Carregando dados…</div></div>;

  const kpis = stats?.kpis || {};
  const semDados = (kpis.totalReunioes || 0) === 0;

  const allSeriesKeys = ['total', 'totalJovens', 'recitaram', ...Object.keys(CAT_LABELS)];

  return (
    <div className="rj-page">
      <button className="rj-back" onClick={() => navigate('/reuniao-jovens')}>
        <FiArrowLeft /> Voltar
      </button>

      <header className="rj-page-header">
        <div>
          <h1><FiBarChart2 className="rj-icon-title" /> Dashboard Analítico</h1>
          <p>Evolução do comparecimento, recitativos e desempenho por congregação</p>
        </div>
        <div className="rj-header-actions rj-dash-filters">
          {congDisponiveis.length > 0 && (
            <div className="rj-filtros rj-filtros-inline rj-filtros-periodo">
              <FiMapPin />
              <select value={congFiltro} onChange={(e) => setCongFiltro(e.target.value)}>
                <option value="">Todas congregações</option>
                {congDisponiveis.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
          <div className="rj-filtros rj-filtros-inline rj-filtros-periodo">
            <FiFilter />
            <select value={periodo} onChange={(e) => setPeriodo(e.target.value)}>
              <option value="3m">3 meses</option>
              <option value="6m">6 meses</option>
              <option value="12m">12 meses</option>
              <option value="24m">24 meses</option>
              <option value="todos">Todo período</option>
            </select>
          </div>
        </div>
      </header>

      {semDados ? (
        <div className="rj-empty">
          <FiBarChart2 size={28} />
          <h3>Sem dados ainda</h3>
          <p>Preencha os relatórios das reuniões para ver as métricas aqui.</p>
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="rj-kpis rj-kpis-dashboard">
            <div className="rj-kpi"><FiCalendar /><div>
              <span className="rj-kpi-num">{kpis.totalReunioes}</span>
              <span className="rj-kpi-lbl">Reuniões</span>
            </div></div>
            <div className="rj-kpi"><FiUsers /><div>
              <span className="rj-kpi-num">{kpis.mediaCompareceram}</span>
              <span className="rj-kpi-lbl">Média / reunião</span>
            </div></div>
            <div className="rj-kpi"><FiBookOpen /><div>
              <span className="rj-kpi-num">
                {kpis.totalReunioes > 0 ? Math.round(kpis.totalRecitaram / kpis.totalReunioes) : 0}
              </span>
              <span className="rj-kpi-lbl">Média recitaram</span>
            </div></div>
            <div className="rj-kpi"><FiAward /><div>
              <span className="rj-kpi-num">{kpis.taxaRecitacao}%</span>
              <span className="rj-kpi-lbl">Taxa de recitação</span>
            </div></div>
          </div>

          {/* ══════ GRÁFICO DE LINHA — Evolução por reunião ══════ */}
          {chartData && (
            <section className="rj-section">
              <div className="rj-chart-header">
                <div>
                  <h2><FiTrendingUp /> Evolução por reunião</h2>
                  <p className="rj-section-sub">
                    Cada ponto representa uma reunião de jovens (1 domingo). Selecione as séries que deseja visualizar.
                  </p>
                </div>
                <div className="rj-chart-actions">
                  <select
                    className="rj-chart-select"
                    value={chartPeriodo}
                    onChange={(e) => setChartPeriodo(e.target.value)}
                  >
                    <option value="3m">3 meses</option>
                    <option value="6m">6 meses</option>
                    <option value="12m">12 meses</option>
                    <option value="todos">Todo período</option>
                  </select>
                  <button className="rj-btn-icone" onClick={downloadChart} title="Baixar como PNG">
                    <FiDownload />
                  </button>
                </div>
              </div>

              {/* Chips de séries */}
              <div className="rj-chart-chips">
                {allSeriesKeys.map((key) => {
                  const isActive = seriesSel.includes(key);
                  const cor = SERIES_EXTRA[key]?.cor || CAT_COR[key] || '#71717a';
                  const lbl = SERIES_EXTRA[key]?.label || CAT_LABELS[key] || key;
                  return (
                    <button
                      key={key}
                      className={`rj-chart-chip ${isActive ? 'rj-chart-chip--on' : ''}`}
                      style={isActive ? { background: cor, borderColor: cor, color: '#fff' } : { borderColor: cor, color: cor }}
                      onClick={() => toggleSerie(key)}
                    >
                      {lbl}
                    </button>
                  );
                })}
              </div>

              {/* Legenda custom */}
              <div className="rj-chart-legend">
                {chartData.datasets.map((ds) => (
                  <span key={ds.label} className="rj-chart-legend-item">
                    <span className="rj-chart-legend-dot" style={{ background: ds.borderColor }} />
                    {ds.label}
                  </span>
                ))}
              </div>

              <div className="rj-chart-container">
                <Line ref={chartRef} data={chartData} options={chartOptions} />
              </div>
            </section>
          )}

          {/* Comparativo por congregação */}
          {congregacoes.length > 1 && (
            <section className="rj-section">
              <h2>Comparativo por congregação</h2>
              <p className="rj-section-sub">
                <strong>Média por reunião</strong> — cada barra representa a média de pessoas por encontro, não soma acumulada.
              </p>

              {liderCong && (
                <div className="rj-lider">
                  <FiAward />
                  <div>
                    <strong>{liderCong.congregacao}</strong> lidera com média de{' '}
                    <strong>{liderCong.mediaCompareceram}</strong> presentes e{' '}
                    <strong>{liderCong.mediaRecitaram}</strong> recitando por encontro ({liderCong.reunioes} reuniões).
                  </div>
                </div>
              )}

              <div className="rj-bar-list">
                {congregacoes.map((c) => {
                  const pct = (c.mediaCompareceram / maxCong) * 100;
                  const taxa = c.totalCompareceram > 0
                    ? Math.round((c.totalRecitaram / c.totalCompareceram) * 100) : 0;
                  return (
                    <div key={c.congregacao} className="rj-bar-item">
                      <div className="rj-bar-head">
                        <span className="rj-bar-name">{c.congregacao}</span>
                        <span className="rj-bar-vals">
                          <strong>Média {c.mediaCompareceram}</strong>
                          <span className="rj-muted"> · {c.reunioes} reuniões · {taxa}% recitam</span>
                        </span>
                      </div>
                      <div className="rj-bar-track">
                        <div className="rj-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="rj-bar-sub-line">
                        <span>Média recitaram: <strong>{c.mediaRecitaram}</strong></span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Resumo de congregação individual */}
          {congregacoes.length === 1 && (
            <section className="rj-section">
              <h2>{congregacoes[0].congregacao}</h2>
              <p className="rj-section-sub">Resumo da congregação selecionada</p>
              <div className="rj-cong-resumo">
                <div className="rj-cong-stat">
                  <span className="rj-cong-stat-num">{congregacoes[0].reunioes}</span>
                  <span className="rj-cong-stat-lbl">Reuniões</span>
                </div>
                <div className="rj-cong-stat">
                  <span className="rj-cong-stat-num">{congregacoes[0].mediaCompareceram}</span>
                  <span className="rj-cong-stat-lbl">Média presentes</span>
                </div>
                <div className="rj-cong-stat">
                  <span className="rj-cong-stat-num">{congregacoes[0].mediaRecitaram}</span>
                  <span className="rj-cong-stat-lbl">Média recitaram</span>
                </div>
                <div className="rj-cong-stat">
                  <span className="rj-cong-stat-num">
                    {congregacoes[0].totalCompareceram > 0
                      ? Math.round((congregacoes[0].totalRecitaram / congregacoes[0].totalCompareceram) * 100)
                      : 0}%
                  </span>
                  <span className="rj-cong-stat-lbl">Taxa recitação</span>
                </div>
              </div>
            </section>
          )}

          {/* Média por categoria */}
          <section className="rj-section">
            <h2>Média por categoria</h2>
            <p className="rj-section-sub">
              Média de pessoas por reunião em cada grupo — não é soma acumulada.
            </p>

            <div className="rj-cat-grid">
              {(stats?.porCategoria || []).map((c) => {
                const pct = (c.mediaCompareceram / maxCatMedia) * 100;
                return (
                  <div key={c.categoria} className="rj-cat-card">
                    <div className="rj-cat-header">
                      <span className="rj-cat-pill" style={{ background: CAT_COR[c.categoria] }}>
                        {CAT_LABELS[c.categoria]}
                      </span>
                      {c.categoria !== 'CASADOS' && (
                        <span className="rj-muted">{c.taxa}% recitam</span>
                      )}
                    </div>
                    <div className="rj-cat-numero">{c.mediaCompareceram}</div>
                    {c.categoria !== 'CASADOS' && (
                      <div className="rj-muted rj-cat-rec">
                        {c.mediaRecitaram} recitaram (média)
                      </div>
                    )}
                    <div className="rj-bar-track rj-bar-track-mini">
                      <div
                        className="rj-bar-fill"
                        style={{ width: `${pct}%`, background: CAT_COR[c.categoria] }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Evolução mensal (barras) */}
          {evolucaoChart && (
            <section className="rj-section">
              <div className="rj-chart-header">
                <div>
                  <h2>Evolução mensal</h2>
                  <p className="rj-section-sub">
                    {metricaMensal === 'recitaram'
                      ? 'Média de pessoas que recitaram por reunião em cada mês.'
                      : 'Média de pessoas por reunião em cada mês. Valores menores podem indicar feriados ou menor adesão.'}
                  </p>
                </div>
                <div className="rj-chart-chips rj-chart-chips--inline" role="group" aria-label="Métrica do gráfico mensal">
                  <button
                    type="button"
                    className={`rj-chart-chip ${metricaMensal === 'presentes' ? 'rj-chart-chip--on' : ''}`}
                    style={metricaMensal === 'presentes'
                      ? { background: '#18181b', borderColor: '#18181b', color: '#fff' }
                      : { borderColor: '#18181b', color: '#18181b' }}
                    onClick={() => setMetricaMensal('presentes')}
                  >
                    Presentes
                  </button>
                  <button
                    type="button"
                    className={`rj-chart-chip ${metricaMensal === 'recitaram' ? 'rj-chart-chip--on' : ''}`}
                    style={metricaMensal === 'recitaram'
                      ? { background: '#22c55e', borderColor: '#22c55e', color: '#fff' }
                      : { borderColor: '#22c55e', color: '#15803d' }}
                    onClick={() => setMetricaMensal('recitaram')}
                  >
                    Recitaram
                  </button>
                </div>
              </div>
              <div className="rj-grafico-mensal">
                {evolucaoChart.periodos.map((p) => {
                  const val = evolucaoChart.valorDe(p);
                  const altura = (val / evolucaoChart.maxValor) * 100;
                  const labelMetrica = metricaMensal === 'recitaram' ? 'recitaram' : 'presentes';
                  return (
                    <div
                      key={p.periodo}
                      className="rj-coluna"
                      title={`${formatarMes(p.periodo)}: média ${val} ${labelMetrica} em ${p.reunioes} reuniões`}
                    >
                      <div className="rj-coluna-valor">{val}</div>
                      <div className="rj-coluna-bar-wrap">
                        <div
                          className={`rj-coluna-bar ${metricaMensal === 'recitaram' ? 'rj-coluna-bar--rec' : ''}`}
                          style={{ height: `${altura}%` }}
                        />
                      </div>
                      <div className="rj-coluna-label">{formatarMes(p.periodo)}</div>
                      <div className="rj-coluna-sub">{p.reunioes} reun.</div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Heatmap congregação × mês */}
          {evolucaoChart && congregacoes.length > 0 && (
            <section className="rj-section">
              <h2>Média por congregação × mês</h2>
              <p className="rj-section-sub">
                Cada célula mostra a <strong>média de presentes por reunião</strong> naquele mês. Quanto mais escuro, maior a média.
              </p>
              <div className="rj-heatmap-wrap">
                <table className="rj-heatmap">
                  <thead>
                    <tr>
                      <th></th>
                      {evolucaoChart.periodos.map((p) => (
                        <th key={p.periodo}>{formatarMes(p.periodo)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {congregacoes.map((c) => {
                      const maxCM = Math.max(
                        ...evolucaoChart.periodos.map((p) => p.congMedias?.[c.congregacao] || 0), 1
                      );
                      return (
                        <tr key={c.congregacao}>
                          <td className="rj-heatmap-cong">{c.congregacao}</td>
                          {evolucaoChart.periodos.map((p) => {
                            const v = p.congMedias?.[c.congregacao] || 0;
                            const nReun = p.congReun?.[c.congregacao] || 0;
                            const pctH = maxCM > 0 ? v / maxCM : 0;
                            const op = v === 0 ? 0.05 : 0.15 + 0.85 * pctH;
                            return (
                              <td
                                key={p.periodo}
                                className="rj-heatmap-cell"
                                style={{ background: `rgba(34, 197, 94, ${op})` }}
                                title={`${c.congregacao} – ${formatarMes(p.periodo)}: média ${v} (${nReun} reun.)`}
                              >
                                {v > 0 ? v : ''}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default ReuniaoJovensDashboard;
